import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
    getTeam, updateTeam, getTeamMembers, addTeamMember, updateTeamMember,
    removeTeamMember, getTeamProjects, shareProject, unshareProject,
    getProjects, Team, TeamMember, TeamProject, Project
} from '../../services/api';
import { useAuth } from '../../contexts/AuthContext';
import { GlassCard } from '../ui/GlassCard';
import { GlassButton } from '../ui/GlassButton';
import { GlassInput } from '../ui/GlassInput';
import { GlassSelect } from '../ui/GlassSelect';
import { GlassBadge } from '../ui/GlassBadge';
import { GlassModal } from '../ui/GlassModal';
import './Teams.css';

/** Normalize FastAPI error detail — may be a string or a Pydantic validation error array */
const getApiError = (e: any, fallback: string): string => {
  const detail = e?.response?.data?.detail;
  if (!detail) return fallback;
  if (typeof detail === 'string') return detail;
  if (Array.isArray(detail)) {
    return detail.map((d: any) => d?.msg ?? JSON.stringify(d)).join('; ');
  }
  return fallback;
};


const TeamDetail: React.FC = () => {
    const { teamId } = useParams<{ teamId: string }>();
    const { user } = useAuth();
    const navigate = useNavigate();

    const [team, setTeam] = useState<Team | null>(null);
    const [members, setMembers] = useState<TeamMember[]>([]);
    const [teamProjects, setTeamProjects] = useState<TeamProject[]>([]);
    const [allProjects, setAllProjects] = useState<Project[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState('');

    // Edit team state
    const [isEditing, setIsEditing] = useState(false);
    const [editName, setEditName] = useState('');
    const [editDescription, setEditDescription] = useState('');

    // Add member state
    const [showAddMember, setShowAddMember] = useState(false);
    const [newMemberEmail, setNewMemberEmail] = useState('');
    const [newMemberRole, setNewMemberRole] = useState('viewer');
    const [isAddingMember, setIsAddingMember] = useState(false);

    // Share project state
    const [showShareProject, setShowShareProject] = useState(false);
    const [selectedProjectId, setSelectedProjectId] = useState('');
    const [selectedPermission, setSelectedPermission] = useState('edit');
    const [isSharingProject, setIsSharingProject] = useState(false);

    const isOwner = team?.owner_id === user?.user_id;
    const isAdmin = isOwner || members.find(m => m.user_id === user?.user_id)?.role === 'admin';

    const loadData = async () => {
        if (!teamId) return;

        setIsLoading(true);
        setError('');
        try {
            const [teamData, membersData, projectsData, allProjectsData] = await Promise.all([
                getTeam(teamId),
                getTeamMembers(teamId),
                getTeamProjects(teamId),
                getProjects()
            ]);
            setTeam(teamData);
            setMembers(membersData);
            setTeamProjects(projectsData);
            setAllProjects(allProjectsData);
            setEditName(teamData.name);
            setEditDescription(teamData.description || '');
        } catch (err: any) {
            setError(getApiError(err, 'Failed to load team details'));
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadData();
    }, [teamId]);

    const handleSaveTeam = async () => {
        if (!teamId || !editName.trim()) return;

        try {
            const updated = await updateTeam(teamId, {
                name: editName.trim(),
                description: editDescription.trim() || undefined
            });
            setTeam(updated);
            setIsEditing(false);
        } catch (err: any) {
            setError(getApiError(err, 'Failed to update team'));
        }
    };

    const handleAddMember = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!teamId || !newMemberEmail.trim()) return;

        setIsAddingMember(true);
        try {
            const member = await addTeamMember(teamId, {
                email: newMemberEmail.trim(),
                role: newMemberRole
            });
            setMembers(prev => [...prev, member]);
            setShowAddMember(false);
            setNewMemberEmail('');
            setNewMemberRole('viewer');
        } catch (err: any) {
            setError(getApiError(err, 'Failed to add member'));
        } finally {
            setIsAddingMember(false);
        }
    };

    const handleUpdateMemberRole = async (userId: string, newRole: string) => {
        if (!teamId) return;

        try {
            await updateTeamMember(teamId, userId, { role: newRole });
            setMembers(prev => prev.map(m =>
                m.user_id === userId ? { ...m, role: newRole } : m
            ));
        } catch (err: any) {
            setError(getApiError(err, 'Failed to update member role'));
        }
    };

    const handleRemoveMember = async (userId: string, memberName: string) => {
        if (!teamId) return;
        if (!window.confirm(`Remove ${memberName} from the team?`)) return;

        try {
            await removeTeamMember(teamId, userId);
            setMembers(prev => prev.filter(m => m.user_id !== userId));
        } catch (err: any) {
            setError(getApiError(err, 'Failed to remove member'));
        }
    };

    const handleShareProject = async (e?: React.FormEvent) => {
        e?.preventDefault();
        if (!teamId || !selectedProjectId) return;

        setIsSharingProject(true);
        try {
            const shared = await shareProject(teamId, {
                project_id: selectedProjectId,
                permission_level: selectedPermission
            });
            // Update or add the shared project
            setTeamProjects(prev => {
                const existing = prev.find(p => p.project_id === selectedProjectId);
                if (existing) {
                    return prev.map(p => p.project_id === selectedProjectId ? shared : p);
                }
                return [...prev, shared];
            });
            setShowShareProject(false);
            setSelectedProjectId('');
            setSelectedPermission('edit');
        } catch (err: any) {
            setError(getApiError(err, 'Failed to share project'));
        } finally {
            setIsSharingProject(false);
        }
    };

    const handleUnshareProject = async (projectId: string, projectName: string) => {
        if (!teamId) return;
        if (!window.confirm(`Remove "${projectName}" from this team?`)) return;

        try {
            await unshareProject(teamId, projectId);
            setTeamProjects(prev => prev.filter(p => p.project_id !== projectId));
        } catch (err: any) {
            setError(getApiError(err, 'Failed to unshare project'));
        }
    };

    const getRoleBadgeVariant = (role: string): 'success' | 'warning' | 'info' | 'default' => {
        switch (role) {
            case 'owner':
                return 'warning';
            case 'admin':
                return 'success';
            case 'editor':
                return 'info';
            default:
                return 'default';
        }
    };

    const getPermissionBadgeVariant = (permission: string): 'success' | 'warning' | 'info' | 'default' => {
        switch (permission) {
            case 'admin':
                return 'success';
            case 'edit':
                return 'info';
            default:
                return 'default';
        }
    };

    if (isLoading) {
        return (
            <div className="team-detail-page">
                <GlassCard variant="default" padding="lg" className="loading-card">
                    <div className="loading">Loading team...</div>
                </GlassCard>
            </div>
        );
    }

    if (!team) {
        return (
            <div className="team-detail-page">
                <GlassCard variant="default" padding="lg" className="error-card">
                    <div className="error-message">Team not found</div>
                    <GlassButton variant="primary" onClick={() => navigate('/teams')} style={{ marginTop: '1rem' }}>
                        Back to Teams
                    </GlassButton>
                </GlassCard>
            </div>
        );
    }

    // Filter out already shared projects
    const availableProjects = allProjects.filter(
        p => !teamProjects.find(tp => tp.project_id === p.project_id)
    );

    const roleOptions = [
        { value: 'viewer', label: 'Viewer' },
        { value: 'editor', label: 'Editor' },
        { value: 'admin', label: 'Admin' },
    ];

    const permissionOptions = [
        { value: 'view', label: 'View - Team can view the project' },
        { value: 'edit', label: 'Edit - Team can edit the project' },
        { value: 'admin', label: 'Admin - Team has full control' },
    ];

    const projectOptions = availableProjects.map(p => ({
        value: p.project_id,
        label: p.name
    }));

    return (
        <div className="team-detail-page">
            <GlassButton
                variant="ghost"
                size="sm"
                onClick={() => navigate('/teams')}
                className="back-btn"
            >
                ← Back to Teams
            </GlassButton>

            {error && (
                <GlassCard variant="default" padding="md" className="error-card">
                    <div className="error-message">{error}</div>
                </GlassCard>
            )}

            {/* Team Header */}
            <GlassCard variant="strong" padding="lg" className="team-header-card">
                {isEditing ? (
                    <div className="team-edit-form">
                        <GlassInput
                            label="Team Name"
                            value={editName}
                            onChange={setEditName}
                            placeholder="Team name"
                        />
                        <div style={{ marginTop: 'var(--space-3)' }}>
                            <label className="form-label">Description</label>
                            <textarea
                                value={editDescription}
                                onChange={e => setEditDescription(e.target.value)}
                                placeholder="Team description"
                                rows={2}
                                className="glass-textarea"
                            />
                        </div>
                        <div className="edit-actions">
                            <GlassButton variant="ghost" onClick={() => setIsEditing(false)}>
                                Cancel
                            </GlassButton>
                            <GlassButton variant="primary" onClick={handleSaveTeam}>
                                Save Changes
                            </GlassButton>
                        </div>
                    </div>
                ) : (
                    <div className="team-header-content">
                        <div className="team-identity">
                            <div className="team-avatar-large">
                                {team.name.charAt(0).toUpperCase()}
                            </div>
                            <div className="team-info">
                                <h1>{team.name}</h1>
                                {team.description && <p className="team-desc">{team.description}</p>}
                            </div>
                        </div>
                        {isAdmin && (
                            <GlassButton variant="ghost" onClick={() => setIsEditing(true)}>
                                Edit
                            </GlassButton>
                        )}
                    </div>
                )}
            </GlassCard>

            {/* Members Section */}
            <GlassCard variant="default" padding="lg" className="team-section-card">
                <div className="section-header">
                    <h2>Members ({members.length})</h2>
                    {isAdmin && (
                        <GlassButton variant="primary" size="sm" onClick={() => setShowAddMember(true)}>
                            + Add Member
                        </GlassButton>
                    )}
                </div>

                <div className="members-list">
                    {members.map(member => (
                        <div key={member.id} className="member-item">
                            <div className="member-avatar">
                                {(member.full_name?.charAt(0) || member.username?.charAt(0) || member.email?.charAt(0) || '?').toUpperCase()}
                            </div>
                            <div className="member-info">
                                <span className="member-name">
                                    {member.full_name || member.username || member.email}
                                </span>
                                <span className="member-email">{member.email}</span>
                            </div>
                            <div className="member-actions">
                                {isAdmin && member.role !== 'owner' ? (
                                    <GlassSelect
                                        options={roleOptions}
                                        value={member.role}
                                        onChange={(value) => handleUpdateMemberRole(member.user_id, value)}
                                        size="sm"
                                    />
                                ) : (
                                    <GlassBadge variant={getRoleBadgeVariant(member.role)} size="sm">
                                        {member.role}
                                    </GlassBadge>
                                )}
                                {isAdmin && member.role !== 'owner' && member.user_id !== user?.user_id && (
                                    <GlassButton
                                        variant="danger"
                                        size="sm"
                                        onClick={() => handleRemoveMember(member.user_id, member.full_name || member.username || member.email || 'this member')}
                                    >
                                        Remove
                                    </GlassButton>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            </GlassCard>

            {/* Projects Section */}
            <GlassCard variant="default" padding="lg" className="team-section-card">
                <div className="section-header">
                    <h2>Shared Projects ({teamProjects.length})</h2>
                    {isAdmin && availableProjects.length > 0 && (
                        <GlassButton variant="primary" size="sm" onClick={() => setShowShareProject(true)}>
                            + Share Project
                        </GlassButton>
                    )}
                </div>

                {teamProjects.length === 0 ? (
                    <div className="empty-section">
                        <p>No projects shared with this team yet.</p>
                    </div>
                ) : (
                    <div className="projects-list">
                        {teamProjects.map(tp => (
                            <div key={tp.id} className="project-item">
                                <div className="team-project-info">
                                    <span className="team-project-name">{tp.project_name || tp.project_id}</span>
                                    {tp.project_description && (
                                        <span className="team-project-desc">{tp.project_description}</span>
                                    )}
                                </div>
                                <div className="project-actions">
                                    <GlassBadge variant={getPermissionBadgeVariant(tp.permission_level)} size="sm">
                                        {tp.permission_level}
                                    </GlassBadge>
                                    {isAdmin && (
                                        <GlassButton
                                            variant="ghost"
                                            size="sm"
                                            onClick={() => handleUnshareProject(tp.project_id, tp.project_name || 'this project')}
                                        >
                                            Unshare
                                        </GlassButton>
                                    )}
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </GlassCard>

            {/* Add Member Modal */}
            <GlassModal
                isOpen={showAddMember}
                onClose={() => setShowAddMember(false)}
                title="Add Team Member"
                size="md"
                footer={
                    <>
                        <GlassButton variant="ghost" onClick={() => setShowAddMember(false)}>
                            Cancel
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={handleAddMember}
                            loading={isAddingMember}
                            disabled={isAddingMember || !newMemberEmail.trim()}
                        >
                            Add Member
                        </GlassButton>
                    </>
                }
            >
                <form onSubmit={handleAddMember}>
                    <GlassInput
                        label="Email Address"
                        type="email"
                        value={newMemberEmail}
                        onChange={setNewMemberEmail}
                        placeholder="Enter email address"
                        autoFocus
                    />
                    <div style={{ marginTop: 'var(--space-4)' }}>
                        <GlassSelect
                            label="Role"
                            options={roleOptions}
                            value={newMemberRole}
                            onChange={setNewMemberRole}
                        />
                    </div>
                    <div className="role-descriptions" style={{ marginTop: 'var(--space-3)' }}>
                        <div className="role-desc-item">
                            <GlassBadge variant="default" size="sm">Viewer</GlassBadge>
                            <span>Can view shared projects</span>
                        </div>
                        <div className="role-desc-item">
                            <GlassBadge variant="info" size="sm">Editor</GlassBadge>
                            <span>Can edit shared projects</span>
                        </div>
                        <div className="role-desc-item">
                            <GlassBadge variant="success" size="sm">Admin</GlassBadge>
                            <span>Can manage team members and projects</span>
                        </div>
                    </div>
                </form>
            </GlassModal>

            {/* Share Project Modal */}
            <GlassModal
                isOpen={showShareProject}
                onClose={() => setShowShareProject(false)}
                title="Share Project with Team"
                size="md"
                footer={
                    <>
                        <GlassButton variant="ghost" onClick={() => setShowShareProject(false)}>
                            Cancel
                        </GlassButton>
                        <GlassButton
                            variant="primary"
                            onClick={handleShareProject}
                            loading={isSharingProject}
                            disabled={isSharingProject || !selectedProjectId}
                        >
                            Share Project
                        </GlassButton>
                    </>
                }
            >
                <form onSubmit={handleShareProject}>
                    <GlassSelect
                        label="Project"
                        options={projectOptions}
                        value={selectedProjectId}
                        onChange={setSelectedProjectId}
                        placeholder="Select a project..."
                    />
                    <div style={{ marginTop: 'var(--space-4)' }}>
                        <GlassSelect
                            label="Permission Level"
                            options={permissionOptions}
                            value={selectedPermission}
                            onChange={setSelectedPermission}
                        />
                    </div>
                    <p style={{ marginTop: 'var(--space-3)', marginBottom: 0, color: 'var(--text-secondary)', fontSize: '0.85rem' }}>
                        Sharing a project also shares its CRM records (View = read-only, Edit/Admin = read &amp; write).
                    </p>
                </form>
            </GlassModal>

            <style>{`
                .team-detail-page {
                    max-width: 1000px;
                    margin: 0 auto;
                    padding: var(--space-4);
                }

                .back-btn {
                    margin-bottom: var(--space-4);
                }

                .loading-card,
                .error-card {
                    text-align: center;
                }

                .loading {
                    color: var(--text-secondary);
                }

                .error-message {
                    color: var(--danger);
                }

                .team-header-card {
                    margin-bottom: var(--space-4);
                }

                .team-edit-form {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-3);
                }

                .edit-actions {
                    display: flex;
                    gap: var(--space-3);
                    margin-top: var(--space-2);
                }

                .team-header-content {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                }

                .team-identity {
                    display: flex;
                    align-items: center;
                    gap: var(--space-4);
                }

                .team-avatar-large {
                    width: 64px;
                    height: 64px;
                    border-radius: var(--radius-full);
                    background: var(--primary-500);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-size: var(--text-2xl);
                    font-weight: var(--font-semibold);
                    border: 3px solid var(--stroke);
                    box-shadow: var(--shadow);
                }

                .team-info h1 {
                    margin: 0 0 var(--space-1) 0;
                    font-size: var(--text-2xl);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .team-desc {
                    color: var(--text-secondary);
                    margin: 0;
                }

                .team-section-card {
                    margin-bottom: var(--space-4);
                }

                .section-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-bottom: var(--space-4);
                    padding-bottom: var(--space-3);
                    border-bottom: 1px solid var(--divider);
                }

                .section-header h2 {
                    margin: 0;
                    font-size: var(--text-lg);
                    font-weight: var(--font-semibold);
                    color: var(--text);
                }

                .empty-section {
                    text-align: center;
                    padding: var(--space-6);
                    color: var(--text-secondary);
                }

                .members-list {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-3);
                }

                .member-item {
                    display: flex;
                    align-items: center;
                    gap: var(--space-3);
                    padding: var(--space-3);
                    background: var(--panel);
                    border: 1px solid var(--stroke);
                    border-radius: var(--radius-lg);
                    transition: all var(--transition-fast);
                }

                .member-item:hover {
                    background: var(--panel-hover);
                }

                .member-avatar {
                    width: 40px;
                    height: 40px;
                    border-radius: var(--radius-full);
                    background: var(--secondary-500);
                    color: white;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    font-weight: var(--font-semibold);
                    font-size: var(--text-base);
                    border: 2px solid var(--stroke);
                    box-shadow: var(--shadow-sm);
                }

                .member-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .member-name {
                    font-weight: var(--font-medium);
                    color: var(--text);
                }

                .member-email {
                    font-size: var(--text-sm);
                    color: var(--text-secondary);
                }

                .member-actions {
                    display: flex;
                    align-items: center;
                    gap: var(--space-3);
                }

                .projects-list {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-3);
                }

                .project-item {
                    display: flex;
                    align-items: center;
                    gap: var(--space-3);
                    padding: var(--space-3);
                    background: var(--panel);
                    border: 1px solid var(--stroke);
                    border-radius: var(--radius-lg);
                    transition: all var(--transition-fast);
                }

                .project-item:hover {
                    background: var(--panel-hover);
                }

                .team-project-info {
                    flex: 1;
                    display: flex;
                    flex-direction: column;
                    gap: 2px;
                }

                .team-project-name {
                    font-weight: var(--font-medium);
                    color: var(--text);
                }

                .team-project-desc {
                    font-size: var(--text-sm);
                    color: var(--text-secondary);
                }

                .project-actions {
                    display: flex;
                    align-items: center;
                    gap: var(--space-3);
                }

                .form-label {
                    display: block;
                    font-size: var(--text-sm);
                    font-weight: var(--font-medium);
                    color: var(--text-secondary);
                    margin-bottom: var(--space-1);
                }

                .glass-textarea {
                    width: 100%;
                    padding: var(--space-3) var(--space-4);
                    font-size: var(--text-base);
                    font-family: var(--font-family);
                    color: var(--text);
                    background: var(--panel);
                    border: 1px solid var(--stroke);
                    border-radius: var(--radius-md);
                    backdrop-filter: blur(20px);
                    -webkit-backdrop-filter: blur(20px);
                    transition: all var(--transition-normal);
                    outline: none;
                    resize: vertical;
                    box-sizing: border-box;
                }

                .glass-textarea:focus {
                    border-color: var(--primary-500);
                    box-shadow: 0 0 0 3px var(--primary-100);
                }

                .role-descriptions {
                    display: flex;
                    flex-direction: column;
                    gap: var(--space-2);
                    padding: var(--space-3);
                    background: var(--panel);
                    border-radius: var(--radius-md);
                    border: 1px solid var(--stroke);
                }

                .role-desc-item {
                    display: flex;
                    align-items: center;
                    gap: var(--space-3);
                    font-size: var(--text-sm);
                    color: var(--text-secondary);
                }

                @media (max-width: 768px) {
                    .team-detail-page {
                        padding: var(--space-2);
                    }

                    .team-header-content {
                        flex-direction: column;
                        gap: var(--space-3);
                        align-items: flex-start;
                    }

                    .team-identity {
                        width: 100%;
                    }

                    .section-header {
                        flex-direction: column;
                        gap: var(--space-2);
                        align-items: flex-start;
                    }

                    .member-item,
                    .project-item {
                        flex-direction: column;
                        align-items: flex-start;
                    }

                    .member-actions,
                    .project-actions {
                        width: 100%;
                        justify-content: flex-end;
                    }
                }
            `}</style>
        </div>
    );
};

export default TeamDetail;
